import { FC } from "react";
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Text,
  VStack,
} from "@chakra-ui/react";
import Link from "next/link";
import { HomeContent, Locale } from "@/constants/landingPages";

interface CtaSectionProps {
  content: HomeContent["cta"];
  locale: Locale;
}

export const CtaSection: FC<CtaSectionProps> = ({ content, locale }) => (
  <Box
    data-section="cta"
    py={{ base: "20", md: "32" }}
    bgGradient="linear(to-br, gray.900, gray.800)"
    color="white"
    position="relative"
    overflow="hidden"
  >
    {/* Decorative background elements */}
    <Box
      position="absolute"
      top="-20%"
      right="-10%"
      width="800px"
      height="800px"
      borderRadius="full"
      bgGradient="linear(to-br, primary, pink.600)"
      opacity="0.15"
      filter="blur(100px)"
    />
    <Box
      position="absolute"
      bottom="-20%"
      left="-10%"
      width="700px"
      height="700px"
      borderRadius="full"
      bgGradient="linear(to-tr, secondary, cyan.600)"
      opacity="0.15"
      filter="blur(100px)"
    />

    <Container maxW="7xl" position="relative" zIndex={1}>
      <VStack spacing="12" textAlign="center">
        {/* Main CTA */}
        <VStack spacing="6" maxW="900px">
          <Heading as="h2" textStyle="heading60" lineHeight="1.1">
            {content.title}
          </Heading>
          <Text fontSize="xl" color="gray.300" lineHeight="1.7" maxW="700px" px={{ base: "4", md: "0" }}>
            {content.subtitle}
          </Text>
        </VStack>

        {/* CTA buttons */}
        <Flex
          gap="4"
          direction={{ base: "column", sm: "row" }}
          justify="center"
        >
          <Button
            as={Link}
            href={`/${locale}#kontakt`}
            size="lg"
            variant="primary"
            px="10"
            py="7"
            fontSize="xl"
            fontWeight="700"
            boxShadow="2xl"
            _hover={{
              transform: "translateY(-3px)",
              boxShadow: "2xl",
            }}
            transition="all 0.3s"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById('kontakt')?.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            {content.button}
          </Button>
        </Flex>

        {/* Trust indicators */}
        <Flex
          mt="8"
          gap={{ base: "6", md: "12" }}
          flexWrap="wrap"
          justify="center"
          align="center"
        >
          <VStack spacing="1">
            <Text fontSize="3xl" fontWeight="900">
              {content.trustIndicators.customizable.value}
            </Text>
            <Text fontSize="sm" color="gray.400">
              {content.trustIndicators.customizable.label}
            </Text>
          </VStack>
          <Box w="1px" h="12" bg="gray.700" display={{ base: "none", md: "block" }} />
          <VStack spacing="1">
            <Text fontSize="3xl" fontWeight="900">
              {content.trustIndicators.delivery.value}
            </Text>
            <Text fontSize="sm" color="gray.400">
              {content.trustIndicators.delivery.label}
            </Text>
          </VStack>
          <Box w="1px" h="12" bg="gray.700" display={{ base: "none", md: "block" }} />
          <VStack spacing="1">
            <Text fontSize="3xl" fontWeight="900">
              {content.trustIndicators.branding.value}
            </Text>
            <Text fontSize="sm" color="gray.400">
              {content.trustIndicators.branding.label}
            </Text>
          </VStack>
        </Flex>

        {/* Bottom message */}
        <Box
          mt="12"
          pt="12"
          borderTop="1px solid"
          borderColor="gray.700"
          w="100%"
        >
          <VStack spacing="6">
            <Heading as="h3" textStyle="heading24" color="white" id="kontakt">
              {content.contact.title}
            </Heading>
            <Text color="gray.300" fontSize="lg" maxW="600px">
              {content.contact.description}
            </Text>
            <Flex gap="6" color="gray.400" fontSize="sm" flexWrap="wrap" justify="center">
              <Flex align="center" gap="2">
                <Box
                  w="10"
                  h="10"
                  borderRadius="full"
                  bg="cyan.800"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Text fontSize="xl">📧</Text>
                </Box>
                <Text fontSize="md" fontWeight="medium">{content.contact.email}</Text>
              </Flex>
            </Flex>
          </VStack>
        </Box>
      </VStack>
    </Container>
  </Box>
);
